import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import {
  differenceInHours,
  differenceInMinutes,
  isPast,
  isSameDay,
  parseISO,
  subHours,
} from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { SHIFT_DETAILS_ATTENDANCE_CONFIRMED_FULL_PATH } from "../../path";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { ConfirmAttendanceBottomSheetContent } from "./ConfirmAttendanceBottomSheetContent";

interface ConfirmAttendanceBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  onCancelShift: () => void;
}

export function ConfirmAttendanceBottomSheet(props: ConfirmAttendanceBottomSheetProps) {
  const { modalState, shiftId, onCancelShift } = props;

  const { shift, isLoading } = useGetShiftDetailsData({ shiftId });

  const worker = useDefinedWorker();
  const history = useHistory();
  const { data: { policy, clipboardScore = 0 } = {} } = useGetClipboardScoreProfile(worker.userId);

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    shift?.agentReq ?? "",
  ]);

  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });

  const { data: shiftBoosts } = useGetShiftBoosts(
    {
      workerId: worker.userId,
      statuses: ["APPLIED"],
    },
    { enabled: shiftBoostConfig.enabled }
  );

  if (!isDefined(shift) || isLoading) {
    return null;
  }

  if (shift.agentId !== worker.userId) {
    // the worker is not assigned to the shift anymore
    return null;
  }

  const shiftStartDate = parseISO(shift.start);
  const isShiftToday = isSameDay(shiftStartDate, new Date());

  if (isPast(shiftStartDate)) {
    return null;
  }

  const appliedShiftBoost = shiftBoosts?.data.find(
    (boost) => boost.relationships.shift?.data.id === shift._id
  );
  const shiftLengthInHours = differenceInMinutes(parseISO(shift.end), shiftStartDate) / 60;
  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    shift.originalAmount,
    appliedShiftBoost?.attributes.amount,
    shiftLengthInHours,
    shift.facility.requiresLunchBreak
  );

  const leadTime = differenceInHours(shiftStartDate, new Date());

  function getLeadTimeForRestriction(): number | undefined {
    const cancelShiftConfigs = (policy?.cancelShift ?? []).sort((a, b) => b.points - a.points);

    for (const cancelShiftConfig of cancelShiftConfigs) {
      if (clipboardScore + (cancelShiftConfig.points ?? 0) <= 0) {
        return cancelShiftConfig.leadTime.at(1) ?? Number.POSITIVE_INFINITY;
      }
    }

    return undefined;
  }

  const leadTimeForRestriction = getLeadTimeForRestriction();

  if (!isDefined(leadTimeForRestriction) || leadTimeForRestriction > leadTime) {
    return null;
  }

  const workerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const restrictionStartTime = subHours(shiftStartDate, leadTimeForRestriction);
  const cancellationTime = formatTimeWithTimezone(restrictionStartTime, workerTimezone, false);
  const targetArrivalTime = isDefined(shift.computedArrival?.targetArrivalTime)
    ? parseISO(shift.computedArrival.targetArrivalTime)
    : shiftStartDate;
  const arrivalTime = formatTimeWithTimezone(targetArrivalTime, workerTimezone, false);
  const onConfirmAttendance = () => {
    logEvent(APP_V2_USER_EVENTS.ATTENDANCE_CONFIRMATION_NUDGE_CONFIRMED, {
      workerId: worker.userId,
      shiftId,
    });
    const path = generatePath(SHIFT_DETAILS_ATTENDANCE_CONFIRMED_FULL_PATH, {
      shiftId,
    });
    modalState.closeModal();
    history.push(path);
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <ConfirmAttendanceBottomSheetContent
        modalState={modalState}
        shift={shift}
        qualification={qualificationNameToDisplayLabelMap.get(shift.agentReq) ?? shift.agentReq}
        arrivalTime={arrivalTime}
        cancellationTime={cancellationTime}
        isShiftToday={isShiftToday}
        shiftStartDate={shift.start}
        shiftEndDate={shift.end}
        payBreakdown={payBreakdown}
        onCancelShift={onCancelShift}
        onConfirmAttendance={onConfirmAttendance}
      />
    </ThemeProvider>
  );
}
